import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { recipes } from '../services/api';
import Avatar from '../components/Avatar';
import '../styles/Recipes.css';

const Recipes = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [recipeList, setRecipeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedDifficulty, setSelectedDifficulty] = useState(searchParams.get('difficulty') || '');
  const [maxTime, setMaxTime] = useState(searchParams.get('maxTime') || '');
  const [selectedTags, setSelectedTags] = useState(searchParams.get('tags') ? searchParams.get('tags').split(',') : []);
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'newest');
  const [selectedAuthor, setSelectedAuthor] = useState(searchParams.get('author') || '');
  const [showFilters, setShowFilters] = useState(false);

  const categories = [
    { value: '', label: 'Összes' },
    { value: 'előétel', label: 'Előételek' },
    { value: 'leves', label: 'Levesek' },
    { value: 'főétel', label: 'Főételek' },
    { value: 'saláta', label: 'Saláták' },
    { value: 'desszert', label: 'Desszertek' },
    { value: 'pékáruk', label: 'Pékáruk' },
    { value: 'italok', label: 'Italok' },
    { value: 'egyéb', label: 'Egyebek' }
  ];
  const difficulties = ['összes', 'könnyű', 'közepes', 'nehéz'];
  const dietTags = [
    { value: 'vegetáriánus', label: '🥬 Vegetáriánus', emoji: '🥬' },
    { value: 'vegán', label: '🌱 Vegán', emoji: '🌱' },
    { value: 'gluténmentes', label: '🌾 Gluténmentes', emoji: '🌾' },
    { value: 'laktózmentes', label: '🥛 Laktózmentes', emoji: '🥛' },
    { value: 'cukormentes', label: '🍬 Cukormentes', emoji: '🍬' }
  ];

  useEffect(() => {
    fetchRecipes();
  }, [selectedCategory, selectedDifficulty, searchTerm, maxTime, selectedTags, sortBy, selectedAuthor]);

  const fetchRecipes = async () => {
    setLoading(true);
    try {
      const params = { approved: true };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (selectedCategory) {
        params.category = selectedCategory;
      }

      if (selectedDifficulty && selectedDifficulty !== 'összes') {
        params.difficulty = selectedDifficulty.toLowerCase();
      }

      if (maxTime) {
        params.maxTime = maxTime;
      }

      // NEW: Diet tags
      if (selectedTags.length > 0) {
        params.tags = selectedTags.join(',');
      }

      // NEW: Sort by
      if (sortBy) {
        params.sortBy = sortBy;
      }

      // NEW: Author filter
      if (selectedAuthor) {
        params.author = selectedAuthor;
      }

      console.log('🔍 API Request Params:', params);
      const response = await recipes.getAll(params);
      console.log('📦 API Response:', response.data.length, 'recipes');

      // Client-side filtering for maxTime (backend doesn't support it yet)
      let filteredRecipes = response.data;
      if (maxTime) {
        const maxMinutes = parseInt(maxTime);
        filteredRecipes = filteredRecipes.filter(recipe =>
          (recipe.prepTime + recipe.cookTime) <= maxMinutes
        );
      }

      console.log('📝 Recipe list sample (first recipe):', filteredRecipes[0]);
      setRecipeList(filteredRecipes);

      // Update URL params
      updateURLParams();
    } catch (error) {
      console.error('Error fetching recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateURLParams = () => {
    const params = {};
    if (searchTerm.trim()) params.search = searchTerm.trim();
    if (selectedCategory) params.category = selectedCategory;
    if (selectedDifficulty && selectedDifficulty !== 'összes') params.difficulty = selectedDifficulty;
    if (maxTime) params.maxTime = maxTime;
    if (selectedTags.length > 0) params.tags = selectedTags.join(',');
    if (sortBy && sortBy !== 'newest') params.sortBy = sortBy;
    if (selectedAuthor) params.author = selectedAuthor;
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecipes();
  };

  const handleCategoryChange = (categoryValue) => {
    setSelectedCategory(categoryValue);
  };

  const handleDifficultyChange = (difficulty) => {
    setSelectedDifficulty(difficulty === 'összes' ? '' : difficulty);
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedDifficulty('');
    setMaxTime('');
    setSelectedTags([]);
    setSortBy('newest');
    setSelectedAuthor('');
    setSearchParams({});
  };

  const hasActiveFilters = searchTerm || selectedCategory || selectedDifficulty || maxTime || selectedTags.length > 0 || (sortBy && sortBy !== 'newest') || selectedAuthor;

  return (
    <div className="recipes-page">
      <div className="container">
        <div className="page-header">
          <h1>Receptek</h1>
          <div className="header-actions">
            <button
              className="btn btn-outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? '🔍 Szűrők elrejtése' : '🔍 Szűrők megjelenítése'}
            </button>
            {hasActiveFilters && (
              <button className="btn btn-secondary" onClick={handleClearFilters}>
                ✖ Szűrők törlése
              </button>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="🔍 Keresés cím vagy hozzávaló alapján..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn btn-primary">Keresés</button>
        </form>

        {/* Filter Panel */}
        {showFilters && (
          <div className="filters-panel">
            {/* Category Filter */}
            <div className="filter-group">
              <h3>Kategória</h3>
              <div className="filter-buttons">
                {categories.map((cat) => (
                  <button
                    key={cat.value}
                    className={`filter-btn ${selectedCategory === cat.value ? 'active' : ''}`}
                    onClick={() => handleCategoryChange(cat.value)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Difficulty Filter */}
            <div className="filter-group">
              <h3>Nehézség</h3>
              <div className="filter-buttons">
                {difficulties.map((diff) => (
                  <button
                    key={diff}
                    className={`filter-btn ${selectedDifficulty === diff || (diff === 'összes' && !selectedDifficulty) ? 'active' : ''}`}
                    onClick={() => handleDifficultyChange(diff)}
                  >
                    {diff.charAt(0).toUpperCase() + diff.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Time Filter */}
            <div className="filter-group">
              <h3>Maximum időtartam</h3>
              <div className="time-filter">
                <select
                  value={maxTime}
                  onChange={(e) => setMaxTime(e.target.value)}
                  className="time-select"
                >
                  <option value="">Bármennyi</option>
                  <option value="30">Max 30 perc</option>
                  <option value="60">Max 1 óra</option>
                  <option value="90">Max 1.5 óra</option>
                  <option value="120">Max 2 óra</option>
                </select>
              </div>
            </div>

            {/* Diet Tags Filter */}
            <div className="filter-group">
              <h3>Diétás szűrők</h3>
              <div className="filter-buttons">
                {dietTags.map((tag) => (
                  <button
                    key={tag.value}
                    className={`filter-btn ${selectedTags.includes(tag.value) ? 'active' : ''}`}
                    onClick={() => handleTagToggle(tag.value)}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort By */}
            <div className="filter-group">
              <h3>Rendezés</h3>
              <div className="sort-filter">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="sort-select"
                >
                  <option value="newest">Legújabb</option>
                  <option value="oldest">Legrégebbi</option>
                  <option value="rating">Legjobb értékelés</option>
                  <option value="popularity">Legnépszerűbb</option>
                  <option value="alphabetical">Ábécé szerint</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Active Author Filter Display */}
        {selectedAuthor && recipeList.length > 0 && recipeList[0]?.author && (
          <div className="active-author-filter">
            <span>Szűrés szerző szerint:</span>
            <div className="author-badge">
              <Avatar
                name={recipeList[0].author.nickname || recipeList[0].author.name}
                imageUrl={recipeList[0].author.profileImage}
                size="small"
              />
              <span>{recipeList[0].author.nickname || recipeList[0].author.name}</span>
              <button
                className="clear-author-btn"
                onClick={() => setSelectedAuthor('')}
                title="Szűrő törlése"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Results Info */}
        {!loading && (
          <div className="results-info">
            <p>{recipeList.length} recept találva</p>
          </div>
        )}

        {loading ? (
          <p className="loading">Betöltés...</p>
        ) : recipeList.length === 0 ? (
          <p className="no-recipes">Nem található recept.</p>
        ) : (
          <div className="recipe-grid">
            {recipeList.map((recipe) => (
              <div key={recipe._id} className="recipe-card card">
                <Link to={`/recipe/${recipe._id}`} className="recipe-card-link">
                  <div className="recipe-card-image-wrapper">
                    {recipe.coverImage ? (
                      <img
                        src={typeof recipe.coverImage === 'object' ? recipe.coverImage.medium : recipe.coverImage}
                        alt={recipe.title}
                        className="recipe-card-image"
                      />
                    ) : (
                      <div className="recipe-card-no-image">📷</div>
                    )}
                  </div>
                  <div className="recipe-card-body">
                    <h3>{recipe.title}</h3>
                    <div className="recipe-meta">
                      <span className="recipe-meta-item">
                        <span className="meta-icon">⚡</span>
                        {recipe.difficulty || 'Könnyű'}
                      </span>
                      <span className="recipe-meta-item">
                        <span className="meta-icon">⏱️</span>
                        {recipe.prepTime + recipe.cookTime} perc
                      </span>
                      <span className="recipe-meta-item">
                        <span className="meta-icon">👥</span>
                        {recipe.servings} adag
                      </span>
                    </div>
                  </div>
                </Link>
                {recipe.author && (
                  <div className="recipe-card-author">
                    <Avatar
                      name={recipe.author.nickname || recipe.author.name}
                      imageUrl={recipe.author.profileImage}
                      size="small"
                    />
                    <span
                      className="recipe-card-author-name clickable"
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedAuthor(recipe.author._id);
                      }}
                      title={`Szűrés ${recipe.author.nickname || recipe.author.name} receptjeire`}
                    >
                      {recipe.author.nickname || recipe.author.name}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Recipes;
